// Today's sky and its contacts with a natal chart. Deterministic, built on
// the same engine as the natal chart (core.js). Nothing here is generated by
// a language model; the Today screen reads these numbers and looks up copy.

import * as Astronomy from "astronomy-engine";
import {
  PLANETS, bodyLongitude, bodySpeed, signOf, signedDelta, separation, mod360,
  lunationPhase, houseOf, formatLon,
} from "./core.js";

const DAY = 86400000;
const addDays = (d, n) => new Date(d.getTime() + n * DAY);

export const ASPECT_ANGLES = [
  { type: "conjunction", angle: 0, cls: "conjunction" },
  { type: "sextile", angle: 60, cls: "soft" },
  { type: "square", angle: 90, cls: "hard" },
  { type: "trine", angle: 120, cls: "soft" },
  { type: "opposition", angle: 180, cls: "hard" },
];

// Orbs for transits are tighter than natal orbs: a transit is "on" while it
// is close to exact. Slow planets get a little more room because they sit
// near exact for weeks.
const TRANSIT_ORB = { moon: 3, sun: 1.5, mercury: 1.5, venus: 1.5, mars: 2, jupiter: 2, saturn: 2, uranus: 1.5, neptune: 1.5, pluto: 1.5 };
export const SLOW = ["jupiter", "saturn", "uranus", "neptune", "pluto"];
export const FAST = ["moon", "sun", "mercury", "venus", "mars"];

// How much each body's transit matters, and how much each natal point does.
const T_WEIGHT = { moon: 1, sun: 2.5, mercury: 2, venus: 2, mars: 3, jupiter: 5, saturn: 7, uranus: 8, neptune: 8, pluto: 9 };
const N_WEIGHT = { sun: 3, moon: 3, asc: 3, mc: 2.5, mercury: 2, venus: 2, mars: 2, jupiter: 1.2, saturn: 1.5, uranus: 1, neptune: 1, pluto: 1 };
const A_WEIGHT = { conjunction: 1, opposition: 0.9, square: 0.9, trine: 0.7, sextile: 0.55 };

const NATAL_TARGETS = ["sun", "moon", "mercury", "venus", "mars", "jupiter", "saturn", "asc", "mc"];

/** Positions, speeds, Moon phase and illumination for an instant. */
export function skyAt(date = new Date()) {
  const bodies = {};
  for (const k of PLANETS) {
    const lon = bodyLongitude(k, date);
    const speed = bodySpeed(k, date);
    const s = signOf(lon);
    bodies[k] = { lon, speed, retrograde: speed < 0, sign: s.name, signKey: s.key, text: formatLon(lon).text };
  }
  const illum = Astronomy.Illumination(Astronomy.Body.Moon, Astronomy.MakeTime(date));
  return {
    date: date.toISOString(),
    bodies,
    lunation: lunationPhase(bodies.sun.lon, bodies.moon.lon),
    moonLit: +illum.phase_fraction.toFixed(3),
  };
}

/** Next instant (within maxDays) a body's daily motion changes sign. */
export function nextStation(body, date = new Date(), maxDays = 400) {
  const start = Math.sign(bodySpeed(body, date));
  let prev = date;
  for (let d = 1; d <= maxDays; d += 1) {
    const t = addDays(date, d);
    if (Math.sign(bodySpeed(body, t)) !== start) {
      let lo = prev.getTime(), hi = t.getTime();
      while (hi - lo > 3600000) {
        const mid = new Date((lo + hi) / 2);
        if (Math.sign(bodySpeed(body, mid)) === start) lo = mid.getTime(); else hi = mid.getTime();
      }
      return { date: new Date(hi), turns: start < 0 ? "direct" : "retrograde" };
    }
    prev = t;
  }
  return null;
}

/** Planets retrograde at `date`, each with the date it turns direct. */
export function retrogradesNow(date = new Date()) {
  return ["mercury", "venus", "mars", "jupiter", "saturn", "uranus", "neptune", "pluto"]
    .filter((k) => bodySpeed(k, date) < 0)
    .map((k) => ({ planet: k, direct: nextStation(k, date, k === "mercury" || k === "venus" || k === "mars" ? 120 : 200)?.date || null }));
}

function natalLon(natal, key) {
  return natal.points[key]?.lon ?? null;
}

// Distance from exact for a given transit body/angle at time t.
function orbAt(body, target, angle, t) {
  return Math.abs(separation(bodyLongitude(body, t), target) - angle);
}

/**
 * Walk outward from `date` while the transit stays within orb. Step sizes
 * suit each body; returns the current pass's start, end, and its closest
 * approach (the exact moment if it perfects inside the pass).
 */
function passWindow(body, target, angle, orb, date) {
  const step = body === "moon" ? 1 / 24 : FAST.includes(body) ? 0.25 : 1;
  const limit = body === "moon" ? 2 : FAST.includes(body) ? 30 : 500;
  let start = date, end = date;
  for (let d = step; d <= limit; d += step) {
    const t = addDays(date, -d);
    if (orbAt(body, target, angle, t) > orb) break;
    start = t;
  }
  for (let d = step; d <= limit; d += step) {
    const t = addDays(date, d);
    if (orbAt(body, target, angle, t) > orb) break;
    end = t;
  }
  // Closest approach inside the pass: sample, then refine.
  let best = date, bestOrb = orbAt(body, target, angle, date);
  const span = (end - start) / DAY;
  const n = Math.max(8, Math.min(200, Math.ceil(span / step)));
  for (let i = 0; i <= n; i++) {
    const t = new Date(start.getTime() + ((end - start) * i) / n);
    const o = orbAt(body, target, angle, t);
    if (o < bestOrb) { bestOrb = o; best = t; }
  }
  let lo = Math.max(start.getTime(), best.getTime() - (span / n) * DAY), hi = Math.min(end.getTime(), best.getTime() + (span / n) * DAY);
  for (let i = 0; i < 40; i++) {
    const a = lo + (hi - lo) / 3, b = hi - (hi - lo) / 3;
    if (orbAt(body, target, angle, new Date(a)) < orbAt(body, target, angle, new Date(b))) hi = b; else lo = a;
  }
  const exact = new Date((lo + hi) / 2);
  return { start, end, exact, exactOrb: +orbAt(body, target, angle, exact).toFixed(3) };
}

/**
 * Transits active at `date` to the natal chart, strongest first.
 * Each: { body, target, type, cls, orb, applying, weight, start, end, exact, exactOrb, isReturn }
 */
export function activeTransits(natal, date = new Date()) {
  const out = [];
  const targetsFor = (body) => {
    // Without a birth time the natal Moon is only known to about ±6.5°, too
    // loose to time a transit to it.
    const base = NATAL_TARGETS.filter((k) => natal.points[k] && (k !== "moon" || natal.hasBirthTime));
    // Slow outer planets also make "checkpoint" transits to themselves.
    return ["uranus", "neptune", "pluto"].includes(body) ? [...base, body] : base;
  };
  for (const body of ["moon", "sun", "mercury", "venus", "mars", ...SLOW]) {
    const lon = bodyLongitude(body, date);
    const speed = bodySpeed(body, date);
    for (const target of targetsFor(body)) {
      const tLon = natalLon(natal, target);
      if (tLon == null) continue;
      // The Moon's daily aspects to the slow natal planets are noise.
      if (body === "moon" && !["sun", "moon", "asc", "mc", "venus", "mars"].includes(target)) continue;
      const sep = separation(lon, tLon);
      for (const asp of ASPECT_ANGLES) {
        const orb = Math.abs(sep - asp.angle);
        if (orb > TRANSIT_ORB[body]) continue;
        const later = separation(lon + speed * 0.05, tLon);
        const w = passWindow(body, tLon, asp.angle, TRANSIT_ORB[body], date);
        const tightness = 1 - orb / TRANSIT_ORB[body];
        // Life-cycle checkpoints (Saturn return, Uranus opposition, Neptune and
        // Pluto squares) outrank everything else in a season; outer-planet
        // sextiles are the mildest contacts.
        const checkpoint = body === target && ["saturn", "uranus", "neptune", "pluto"].includes(body) && asp.cls !== "soft";
        const nW = checkpoint ? 3.2 : (N_WEIGHT[target] || 1);
        const aW = asp.type === "sextile" && ["uranus", "neptune", "pluto"].includes(body) ? 0.4 : A_WEIGHT[asp.type];
        out.push({
          body, target, type: asp.type, cls: asp.cls,
          orb: +orb.toFixed(2),
          applying: Math.abs(later - asp.angle) < orb,
          weight: +(T_WEIGHT[body] * nW * aW * (0.55 + 0.45 * tightness)).toFixed(2),
          checkpoint,
          isReturn: body === target && asp.type === "conjunction",
          ...w,
        });
      }
    }
  }
  return out.sort((a, b) => b.weight - a.weight);
}

// Bisect a sign change of f between two times (ms) down to `tol` ms.
function bisect(f, lo, hi, tol = 60000) {
  const s0 = Math.sign(f(lo));
  while (hi - lo > tol) {
    const mid = (lo + hi) / 2;
    if (Math.sign(f(mid)) === s0) lo = mid; else hi = mid;
  }
  return new Date((lo + hi) / 2);
}

/**
 * Every exact hit in a slow transit's series: the retrograde loop can bring
 * a planet back over the same degree two, three or five times. Scans in
 * three-day steps over ±`span` days (900, or 1200 for the outer planets
 * whose series run longest; well inside even Jupiter's 12-year cycle, so
 * every hit found belongs to this one series). Returns hits in time order,
 * each { date, retrograde }, the in-orb window across all of them, and any
 * station within 1° of the exact degree (a planet turning around on the
 * point, which practitioners read as the strongest form of the transit).
 */
export function transitSeries(t, natal, date = new Date(), span = ["uranus", "neptune", "pluto"].includes(t.body) ? 1200 : 900) {
  const tLon = natal.points[t.target].lon;
  const angle = ASPECT_ANGLES.find((a) => a.type === t.type).angle;
  const lonNow = bodyLongitude(t.body, date);
  // A square or sextile has two exact points; the series is the nearer one.
  const pts = angle === 0 || angle === 180 ? [mod360(tLon + angle)] : [mod360(tLon + angle), mod360(tLon - angle)];
  const p = pts.reduce((a, b) => (Math.abs(signedDelta(lonNow, a)) <= Math.abs(signedDelta(lonNow, b)) ? a : b));
  const f = (ms) => signedDelta(bodyLongitude(t.body, new Date(ms)), p);
  // Three-day steps: even Jupiter moves under 1° in that time. A planet that
  // crosses and recrosses inside three days is stationing right on the
  // degree; that touch is missed here and the pass's closest approach
  // (activeTransits' `exact`) still reports it.
  const STEP = 3;
  const hits = [];
  let prevT = date.getTime() - span * DAY, prev = f(prevT);
  for (let d = -span + STEP; d <= span; d += STEP) {
    const ms = date.getTime() + d * DAY, v = f(ms);
    if (Math.sign(v) !== Math.sign(prev) && Math.abs(v) < 5 && Math.abs(prev) < 5) {
      const at = bisect(f, prevT, ms, 3600000);
      hits.push({ date: at, retrograde: bodySpeed(t.body, at) < 0 });
    }
    prevT = ms; prev = v;
  }
  // In-orb window around the whole series (or just this pass for a near miss).
  const orb = TRANSIT_ORB[t.body];
  const inOrb = (ms) => Math.abs(f(ms)) <= orb;
  let start = t.start, end = t.end;
  if (hits.length) {
    let a = hits[0].date.getTime(), b = hits[hits.length - 1].date.getTime();
    while (inOrb(a - STEP * DAY) && a > date.getTime() - 2 * span * DAY) a -= STEP * DAY;
    while (inOrb(b + STEP * DAY) && b < date.getTime() + 2 * span * DAY) b += STEP * DAY;
    start = new Date(Math.min(a, t.start.getTime()));
    end = new Date(Math.max(b, t.end.getTime()));
  }
  // Stations inside the window: find direction flips on a three-day grid,
  // then time each one with nextStation over the short bracket.
  const stations = [];
  let prevLon = bodyLongitude(t.body, start), prevDir = 0;
  for (let ms = start.getTime() + STEP * DAY; ms <= end.getTime() + STEP * DAY; ms += STEP * DAY) {
    const lon = bodyLongitude(t.body, new Date(ms));
    const dir = Math.sign(signedDelta(lon, prevLon));
    if (prevDir && dir && dir !== prevDir) {
      const st = nextStation(t.body, new Date(ms - 2 * STEP * DAY), 2 * STEP + 1);
      const off = st ? Math.abs(signedDelta(bodyLongitude(t.body, st.date), p)) : 99;
      if (st && off <= 1) stations.push({ ...st, off: +off.toFixed(2) });
    }
    if (dir) prevDir = dir;
    prevLon = lon;
  }
  return { hits, start, end, stations };
}

const MOON_ASPECTS = [0, 60, -60, 90, -90, 120, -120, 180];
const VOC_BODIES = ["sun", "mercury", "venus", "mars", "jupiter", "saturn", "uranus", "neptune", "pluto"];

/**
 * The Moon's current sign stay: when it entered, when it leaves, and when
 * it goes void of course (its last exact Ptolemaic aspect to the Sun or a
 * planet, Mercury through Pluto, before it changes sign). `voidFrom` is the
 * sign entry time when it makes no aspect at all in the sign.
 */
export function moonVoid(date = new Date()) {
  const signAt = (ms) => signOf(bodyLongitude("moon", new Date(ms))).key;
  const now = date.getTime(), here = signAt(now), H = 3600000;
  let a = now, b = now;
  while (signAt(a - H) === here) a -= H;
  while (signAt(b + H) === here) b += H;
  // To the second: an aspect can perfect seconds before the sign changes,
  // and a coarser ingress would make the answer depend on the start hour.
  const entered = bisect((ms) => (signAt(ms) === here ? 1 : -1), a - H, a, 1000);
  const leaves = bisect((ms) => (signAt(ms) === here ? 1 : -1), b, b + H, 1000);
  // Sample every body once per hour across the stay, then bisect only the
  // brackets where an aspect perfects.
  const times = [];
  for (let ms = entered.getTime(); ms < leaves.getTime(); ms += H) times.push(ms);
  times.push(leaves.getTime());
  const moonL = times.map((ms) => bodyLongitude("moon", new Date(ms)));
  let voidFrom = entered;
  for (const body of VOC_BODIES) {
    const bodyL = times.map((ms) => bodyLongitude(body, new Date(ms)));
    for (const asp of MOON_ASPECTS) {
      const f = (ms) => signedDelta(bodyLongitude("moon", new Date(ms)), bodyLongitude(body, new Date(ms)) + asp);
      for (let i = times.length - 1; i > 0; i--) {
        if (times[i] <= voidFrom.getTime()) break;
        const v1 = signedDelta(moonL[i], bodyL[i] + asp), v0 = signedDelta(moonL[i - 1], bodyL[i - 1] + asp);
        if (Math.sign(v0) !== Math.sign(v1) && Math.abs(v0) < 20 && Math.abs(v1) < 20) {
          const at = bisect(f, times[i - 1], times[i], 1000);
          if (at > voidFrom && at < leaves) voidFrom = at;
          break; // latest perfection for this body and angle
        }
      }
    }
  }
  const next = signOf(bodyLongitude("moon", new Date(leaves.getTime() + 60000)));
  return { entered, leaves, voidFrom, nextSign: next.name, nextSignKey: next.key, isVoid: date >= voidFrom && date < leaves };
}

/**
 * What makes the Moon worth a full card on a given local day, if anything:
 * a New or Full Moon that perfects during the day, the Moon crossing one of
 * the four natal angles (birth time needed), or a void of course stretch
 * that covers at least two thirds of the day. `dayStart` and `dayEnd` bound the
 * reader's calendar day. Returns { lunation, angle, voidHours, reason,
 * relevance }; reason is null on an ordinary Moon day.
 */
export const MOON_ANGLES = { asc: 0, mc: 0, dsc: 180, ic: 180 };
export function moonDay(natal, dayStart, dayEnd) {
  const t0 = dayStart.getTime(), t1 = dayEnd.getTime();
  let lunation = null;
  for (const [phase, key] of [[0, "new"], [180, "full"]]) {
    const ev = Astronomy.SearchMoonPhase(phase, Astronomy.MakeTime(dayStart), 1.2);
    if (ev && ev.date.getTime() < t1) {
      lunation = { key, at: ev.date, sign: signOf(bodyLongitude("moon", ev.date)).name };
      break;
    }
  }
  let angle = null;
  if (natal?.hasBirthTime && natal.points?.asc && natal.points?.mc) {
    for (const target of ["asc", "mc", "dsc", "ic"]) {
      const lon = natal.points[target === "dsc" ? "asc" : target === "ic" ? "mc" : target].lon + MOON_ANGLES[target];
      const f = (ms) => signedDelta(lon, bodyLongitude("moon", new Date(ms)));
      const a = f(t0), b = f(t1);
      // The Moon moves about 13 degrees a day, always forward.
      if (a < 0 && b >= 0 && b - a < 30) { angle = { target, at: bisect(f, t0, t1) }; break; }
    }
  }
  // Void hours inside the day, from the sign stays at either end of it.
  const stays = [moonVoid(dayStart)];
  const last = moonVoid(new Date(t1 - 1));
  // The same stay found from two hours bisects to a slightly different
  // ingress, so a second stay counts only when it's an hour or more apart.
  if (Math.abs(last.leaves - stays[0].leaves) > 3600000) stays.push(last);
  const voidMs = stays.reduce((s, v) => s + Math.max(0, Math.min(t1, v.leaves.getTime()) - Math.max(t0, v.voidFrom.getTime())), 0);
  const voidHours = +(voidMs / 3600000).toFixed(1);
  const dayHours = (t1 - t0) / 3600000;
  const reason = lunation ? lunation.key : angle ? "angle" : voidHours >= dayHours * 2 / 3 ? "void" : null;
  const relevance = { new: 2.5, full: 2.5, angle: 2, void: 1.5 }[reason] || 0;
  return { lunation, angle, voidHours, reason, relevance, voc: stays };
}

/**
 * Which natal house each slow planet is crossing, and every house change in
 * the next `days` days, each { date, house }, timed to the hour. A
 * retrograde loop can take a planet back into the previous house before it
 * moves on; the Today screen reads that from the sequence. `horizon` is the
 * end of the look-ahead, so a reading never claims "for good" past it.
 */
export function slowPlanetHouses(natal, date = new Date(), days = 1100) {
  if (!natal.houses) return [];
  const cusps = natal.houses.cusps;
  return SLOW.map((planet) => {
    const houseAt = (ms) => houseOf(bodyLongitude(planet, new Date(ms)), cusps);
    const lon = bodyLongitude(planet, date);
    const house = houseOf(lon, cusps);
    const changes = [];
    let cur = house, prevMs = date.getTime();
    for (let d = 2; d <= days; d += 2) {
      const ms = date.getTime() + d * DAY;
      const h = houseAt(ms);
      if (h !== cur) {
        const was = cur;
        const at = bisect((x) => (houseAt(x) === was ? 1 : -1), prevMs, ms, 3600000);
        changes.push({ date: at, house: h });
        cur = h;
      }
      prevMs = ms;
    }
    return { planet, house, sign: signOf(lon).name, retrograde: bodySpeed(planet, date) < 0, changes, horizon: addDays(date, days) };
  });
}

/** Where the Moon is in your chart today, and the next New and Full Moons. */
export function moonInYourChart(natal, date = new Date()) {
  const moon = bodyLongitude("moon", date);
  const res = { sign: signOf(moon).name, signKey: signOf(moon).key, house: null, next: [] };
  if (natal.houses) res.house = houseOf(moon, natal.houses.cusps);
  const t0 = Astronomy.MakeTime(date);
  // Eclipses are lunations near the Moon's nodes; flag the ones that are.
  // Start a day back so an eclipse peaking minutes before its lunation
  // (with "now" in between) still matches.
  const tBack = Astronomy.MakeTime(addDays(date, -1));
  const solar = Astronomy.SearchGlobalSolarEclipse(tBack);
  const lunar = Astronomy.SearchLunarEclipse(tBack);
  // Natal points an eclipse lands on (within 3°) or directly across from.
  const ECL_POINTS = ["sun", "moon", "mercury", "venus", "mars", "jupiter", "saturn", "asc", "mc"]
    .filter((k) => natal.points[k] && (k !== "moon" || natal.hasBirthTime));
  const touches = (lon) => ({
    on: ECL_POINTS.filter((k) => separation(lon, natal.points[k].lon) <= 3),
    across: ECL_POINTS.filter((k) => separation(lon, natal.points[k].lon) >= 177),
  });
  for (const [phase, label] of [[0, "New Moon"], [180, "Full Moon"]]) {
    const ev = Astronomy.SearchMoonPhase(phase, t0, 32);
    if (!ev) continue;
    const lon = bodyLongitude("moon", ev.date);
    const ecl = phase === 0 ? solar : lunar;
    const isEclipse = ecl && Math.abs(ecl.peak.date - ev.date) < DAY;
    res.next.push({
      label, date: ev.date, sign: signOf(lon).name, text: formatLon(lon).text,
      house: natal.houses ? houseOf(lon, natal.houses.cusps) : null,
      eclipse: isEclipse ? { type: phase === 0 ? "solar" : "lunar", kind: String(ecl.kind), ...touches(lon) } : null,
    });
  }
  res.next.sort((a, b) => a.date - b.date);
  return res;
}

/** The next moment the Sun returns to its natal degree (the solar return). */
export function nextSolarReturn(natal, date = new Date()) {
  const target = natal.points.sun.lon;
  const ahead = mod360(target - bodyLongitude("sun", date));
  // Bracket with the mean motion (±6 days), then bisect on the true position.
  const guess = ahead / 0.9856;
  let lo = addDays(date, Math.max(0, guess - 6)).getTime();
  let hi = addDays(date, Math.min(366, guess + 6)).getTime();
  const past = (t) => mod360(bodyLongitude("sun", new Date(t)) - target) < 180 && mod360(bodyLongitude("sun", new Date(t)) - target) >= 0;
  if (past(lo)) lo = date.getTime();
  for (let i = 0; i < 40 && hi - lo > 60000; i++) {
    const mid = (lo + hi) / 2;
    if (past(mid)) hi = mid; else lo = mid;
  }
  return new Date(hi);
}

/** Whole days until the solar return (0 = it happens within the next 24h). */
export function daysToSolarReturn(natal, date = new Date()) {
  return Math.floor((nextSolarReturn(natal, date) - date) / DAY);
}

/** Stations (a planet turning retrograde or direct) in the next `days` days. */
export function upcomingStations(date = new Date(), days = 30) {
  const out = [];
  for (const k of ["mercury", "venus", "mars", "jupiter", "saturn", "uranus", "neptune", "pluto"]) {
    const st = nextStation(k, date, days);
    if (st) out.push({ planet: k, ...st });
  }
  return out.sort((a, b) => a.date - b.date);
}

export { signedDelta };
